/**
 * LTYPE bridge — convert between the dxf-io `DxfLinetypeEntry` projection
 * and the typed drawing-model `Linetype` schema (PR-LT2, plan §10.3).
 *
 * IMPORT (`importLinetypes`): converts parsed `DxfLinetypeEntry[]` into typed
 * `Linetype` records and MERGES them OVER the standard acad.lin seed (custom
 * wins by CASE-INSENSITIVE name). The file's raw group 40 (`patternLength`) and
 * group 73 (`elementCount`) are preserved as `patternLengthRaw` /
 * `elementCountRaw` so a plain round-trip re-emits them byte-exact. Complex
 * text/shape element STYLE handles (group 340) resolve to `styleName` via a
 * handle→name map built from the TABLES STYLE records, while the raw
 * `styleHandle` is kept for round-trip + the handle-side refcheck.
 *
 * EXPORT (`exportLinetypes`): converts `document.linetypes` back into
 * `DxfLinetypeEntry[]`. ROUND-TRIP-EXACTLY-ONCE (plan §10.3 / Codex#1 F10 +
 * Codex#2 P2-8): re-emit the original `patternLengthRaw` / `elementCountRaw`
 * (group 40 / 73) when present; derive `40 = Σ|len|` / `73 = count` only when
 * the linetype carries no raw value (e.g. a seeded or freshly-edited entry).
 *
 * Round-trip-fidelity fixes (Codex#2 PR-LT2 P2):
 *   FIX-4 — a non-65 group-72 alignment from a dirty/vendor file is preserved in
 *           `alignmentRaw` (the typed `alignment` literal stays 65 per spec) and
 *           re-emitted byte-exact (`alignmentRaw ?? 65`).
 *   FIX-6 — a simple dash/gap/dot element preserves whether the source carried a
 *           group 74 (`hadTypeFlag`): import sets it only when the source had a
 *           74; export re-emits `74/0` only for elements that had one OR for
 *           AutoCAD-canonical entries (`source !== "file"`), so a file element
 *           that genuinely lacked a 74 never gains one.
 *   FIX-B — a record preserves whether the source carried the `100/`
 *           `AcDbLinetypeTableRecord` subclass marker (`hadSubclassMarker`):
 *           import threads the dxf-io flag through; export feeds it back so the
 *           writer re-emits `100` only for records that had it (R12 / minimal
 *           DXFs that omit it stay byte-exact).
 *   FIX-1 — a dirty/vendor record may carry a NEGATIVE group 40. The typed
 *           `patternLength` is `nonnegative()`, so import clamps it to `|raw|`
 *           (keeping the validated model parse-able — F12 no-throw) while the
 *           verbatim negative survives in the UNCONSTRAINED `patternLengthRaw`,
 *           which export re-emits byte-exact.
 *
 * (FIX-5, the persisted-header group-70 count override, lives in the dxf-io
 * writer, which knows the actual emitted record count after FIX-4 filtering.)
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §10.2 / §10.3 / §10.5; AutoCAD DXF LTYPE reference; ezdxf.
 */

import type {
  DxfLinetypeElement,
  DxfLinetypeEntry,
  DxfTextStyleEntry,
} from "@dxf-ai-editor/dxf-io";
import type { Linetype, LinetypeElement } from "./linetype";
import { STANDARD_LINETYPES } from "./linetypeSeed";

/**
 * Build a handle → STYLE name map (lowercased handle key) from the TABLES
 * STYLE records, used to resolve a complex LTYPE element's group-340 STYLE
 * handle into a stable style name.
 */
export function buildStyleHandleToName(
  textStyles: ReadonlyArray<DxfTextStyleEntry>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const ts of textStyles) {
    if (ts.handle && ts.name) map.set(ts.handle.toLowerCase(), ts.name);
  }
  return map;
}

/**
 * Convert one parsed `DxfLinetypeElement` into the typed discriminated-union
 * `LinetypeElement`. The signed group-49 length maps to dash (>0) / gap (<0,
 * magnitude stored) / dot (=0); the group-74 type bit2 (text) / bit4 (shape)
 * promotes it to a complex element carrying its STYLE reference + transform.
 *
 * Complex `length` is stored as the raw signed value (the glyph host is
 * usually a gap, i.e. negative); the simple dash/gap/dot variants normalise
 * to a positive magnitude per the `linetype.ts` schema.
 */
function toLinetypeElement(
  el: DxfLinetypeElement,
  styleHandleToName: Map<string, string>,
): LinetypeElement {
  const type = el.elementType ?? 0;
  const isText = (type & 2) !== 0;
  const isShape = (type & 4) !== 0;

  if (isText || isShape) {
    const styleHandle = el.styleHandle;
    const styleName =
      styleHandle !== undefined
        ? styleHandleToName.get(styleHandle.toLowerCase())
        : undefined;
    // FIX-3 (Codex#2 PR-LT2 P2 — F12 no-throw): the typed complex-element
    // `scale` is `z.number().positive()`. A dirty/vendor COMPLEX LTYPE element
    // may carry a NON-POSITIVE group 46 (0 or negative); assigning it verbatim
    // would make `drawingDocumentSchema.parse` REJECT an otherwise-loadable file
    // (same class as the negative-group-40 FIX-1). Keep the raw 46 in the
    // UNCONSTRAINED `scaleRaw` (export re-emits it byte-exact) and store a SAFE
    // positive `scale`: `|raw|` when positive, else AutoCAD's default 1.0.
    const rawScale = el.scale;
    const safeScale =
      rawScale !== undefined && rawScale > 0 ? rawScale : Math.abs(rawScale ?? 0) || 1;
    const common = {
      length: el.length,
      scale: safeScale,
      // Preserve a NON-POSITIVE raw 46 verbatim for byte-exact re-emit; a normal
      // positive value needs no raw (export falls back to the validated `scale`).
      ...(rawScale !== undefined && rawScale <= 0 ? { scaleRaw: rawScale } : {}),
      rotation: el.rotation ?? 0,
      // group 74 bit1 (value 1) = absolute rotation; otherwise relative. The
      // .lin "upright" (U=) mode is not distinguishable from the DXF type bits
      // alone, so import maps to relative/absolute only.
      rotationMode: ((type & 1) !== 0 ? "absolute" : "relative") as
        | "absolute"
        | "relative",
      offsetX: el.offsetX ?? 0,
      offsetY: el.offsetY ?? 0,
      // FIX (Codex PR-LT2 P2 — OMITTED transform-group round-trip): track the
      // PRESENCE of each optional transform group on the source element so export
      // can re-emit only the groups the file actually carried (mirrors the
      // `hadTypeFlag` FIX-6 pattern). The parser leaves `scale/rotation/offsetX/
      // offsetY` undefined when the corresponding 46/50/44/45 group was absent.
      ...(el.scale !== undefined ? { had46: true } : {}),
      ...(el.rotation !== undefined ? { had50: true } : {}),
      ...(el.offsetX !== undefined ? { had44: true } : {}),
      ...(el.offsetY !== undefined ? { had45: true } : {}),
      ...(styleHandle !== undefined ? { styleHandle } : {}),
      ...(styleName !== undefined ? { styleName } : {}),
    };
    if (isShape) {
      return {
        kind: "shape",
        shapeNumber: el.shapeNumber ?? 0,
        shxResolved: styleName !== undefined,
        ...common,
      };
    }
    return {
      kind: "text",
      text: el.text ?? "",
      ...common,
    };
  }

  // Simple element — sign of group 49 selects dash / gap / dot. FIX-6: preserve
  // whether the source carried a group 74 (`elementType` defined) so export can
  // re-emit `74/0` only for elements that originally had one — older/simple DXF
  // writers omit 74 for plain dash/gap/dot, and re-adding it breaks byte parity.
  const hadTypeFlag = el.elementType !== undefined;
  const flag = hadTypeFlag ? { hadTypeFlag: true } : {};
  if (el.length > 0) return { kind: "dash", length: el.length, ...flag };
  if (el.length < 0) return { kind: "gap", length: -el.length, ...flag };
  return { kind: "dot", length: 0, ...flag };
}

/** Convert one parsed `DxfLinetypeEntry` into a typed `Linetype` record. */
export function dxfLinetypeToDrawingLinetype(
  entry: DxfLinetypeEntry,
  styleHandleToName: Map<string, string>,
): Linetype {
  const elements = entry.elements.map((el) =>
    toLinetypeElement(el, styleHandleToName),
  );
  // patternLength: prefer the file's raw group-40, falling back to the derived
  // Σ|len| when absent (the schema requires a non-negative number).
  const derivedLength = elements.reduce(
    (sum, el) => sum + Math.abs(el.length),
    0,
  );
  // FIX-1 (Codex#2 PR-LT2 P2 — F12 no-throw): the typed `patternLength` is
  // `z.number().nonnegative()`. A dirty/vendor LTYPE record may carry a NEGATIVE
  // group 40; assigning it verbatim would make `drawingDocumentSchema.parse`
  // REJECT an otherwise-legal-but-dirty document (violating F12 "a dirty file
  // must still load, never throw/reject"). Keep the raw negative value in the
  // UNCONSTRAINED `patternLengthRaw` (so export re-emits the original 40 byte-
  // exact) and store a SAFE nonnegative `patternLength` for the validated model:
  // `|raw|` when the file supplied a 40, otherwise the derived Σ|len|.
  const rawPatternLength = entry.patternLength;
  const safePatternLength =
    rawPatternLength !== undefined ? Math.abs(rawPatternLength) : derivedLength;
  const lt: Linetype = {
    name: entry.name,
    description: entry.description ?? "",
    flags: entry.flags ?? 0,
    alignment: 65,
    // FIX-4 — group 72 is spec-always-65, so the typed `alignment` literal stays
    // 65; preserve a non-65 raw value (rare dirty/vendor file) in `alignmentRaw`
    // so the writer can re-emit the file's original 72 byte-exact on round-trip.
    ...(entry.alignment !== undefined && entry.alignment !== 65
      ? { alignmentRaw: entry.alignment }
      : {}),
    patternLength: safePatternLength,
    elements,
    ...(rawPatternLength !== undefined
      ? { patternLengthRaw: rawPatternLength }
      : {}),
    ...(entry.elementCount !== undefined
      ? { elementCountRaw: entry.elementCount }
      : {}),
    // FIX-4 — this record came from the file's LTYPE table, so it is always
    // emitted on round-trip (never treated as a droppable synthetic-seed entry).
    source: "file",
    ...(entry.handle !== undefined ? { handle: entry.handle } : {}),
    ...(entry.ownerHandle !== undefined
      ? { ownerHandle: entry.ownerHandle }
      : {}),
    // FIX-B — preserve whether the source carried the `100` subclass marker so
    // a full document round-trip stays byte-exact for R12 / minimal files.
    ...(entry.hadSubclassMarker !== undefined
      ? { hadSubclassMarker: entry.hadSubclassMarker }
      : {}),
    // Carry vendor / unrecognised group codes through the bridge so an
    // import→export round-trip re-emits them (Codex#2 P2). Copy into the
    // schema's `{ code, value }` shape (structurally identical to DxfGroup).
    ...(entry.extraGroups && entry.extraGroups.length > 0
      ? {
          extraGroups: entry.extraGroups.map((g) => ({
            code: g.code,
            value: g.value,
          })),
        }
      : {}),
  };
  return lt;
}

/**
 * Convert parsed `DxfLinetypeEntry[]` into a NAME-keyed `Record<Linetype>`
 * MERGED over the standard acad.lin seed. Custom entries win by
 * CASE-INSENSITIVE name (a provided `Dashed` replaces the seed's `DASHED`,
 * matching AutoCAD's case-insensitive linetype names — plan §10.2). When the
 * file supplies no LTYPE records the seed is returned unchanged.
 */
export function importLinetypes(
  entries: ReadonlyArray<DxfLinetypeEntry>,
  textStyles: ReadonlyArray<DxfTextStyleEntry>,
): Record<string, Linetype> {
  // The seed entries are synthetic (NOT in the file). Tag them `source:"seed"`
  // so `exportLinetypes` can skip the unused ones on round-trip (FIX-4). The
  // seed itself must STILL be merged in for the linetype refcheck (PR-LT1).
  const seed = tagSeed(structuredClone(STANDARD_LINETYPES));
  if (entries.length === 0) return seed;

  const styleHandleToName = buildStyleHandleToName(textStyles);
  const provided: Record<string, Linetype> = {};
  const providedLc = new Set<string>();
  for (const entry of entries) {
    if (!entry.name) continue;
    // `dxfLinetypeToDrawingLinetype` already stamps `source:"file"`.
    provided[entry.name] = dxfLinetypeToDrawingLinetype(
      entry,
      styleHandleToName,
    );
    providedLc.add(entry.name.toLowerCase());
  }

  const out: Record<string, Linetype> = {};
  // Keep seed entries NOT overridden (case-insensitively) by `provided`.
  for (const [k, val] of Object.entries(seed)) {
    if (!providedLc.has(k.toLowerCase())) out[k] = val;
  }
  // Provided wins — provided's own key/casing is preserved.
  for (const [k, val] of Object.entries(provided)) out[k] = val;
  return out;
}

/**
 * Stamp every entry of a freshly-cloned seed record with `source:"seed"` so the
 * export filter (FIX-4) can drop unused synthetic-seed linetypes on round-trip.
 * Mutates + returns the same object (the caller passes a fresh `structuredClone`).
 */
function tagSeed(seed: Record<string, Linetype>): Record<string, Linetype> {
  for (const lt of Object.values(seed)) lt.source = "seed";
  return seed;
}

/* ────────────────────────────────────────────────────────────────────────
 * EXPORT — Linetype → DxfLinetypeEntry.
 * ──────────────────────────────────────────────────────────────────────── */

/** Reserved linetype names that always carry 0 elements. */
const RESERVED_LINETYPE_NAMES = new Set([
  "bylayer",
  "byblock",
  "continuous",
]);

/**
 * Resolve a complex element's STYLE handle (group 340) for export.
 *
 * (a) a raw `styleHandle` is preserved verbatim (the original file's handle);
 * (b) failing that, resolve the `styleName` → a text-style handle via
 *     `styleNameToHandle` — only possible when the caller supplies a name→
 *     handle map. NOTE (Codex#2 P2 / PR-LT2 limitation): the drawing-model
 *     `TextStyle` schema currently has NO `handle` field, so the export path
 *     (`exportLinetypes`, fed only `document.linetypes` + the typed
 *     `StyleTables`) can build NO such map today — `styleNameToHandle` is
 *     always empty. Resolving name→handle would require adding a handle to
 *     `TextStyle`, which is OUT of PR-LT2 scope. The threading is in place so
 *     the moment `TextStyle` gains a handle, (b) lights up with no further
 *     change here.
 * (c) when NEITHER yields a handle we DO NOT silently set the 74 type bit
 *     with a dangling (missing) 340. The element keeps its type bit + text /
 *     transform data (so the glyph + S=/R=/X=/Y= survive round-trip and an
 *     external tool holding the original handle map can re-bind), but the
 *     omission of 340 is now EXPLICIT + documented rather than a silent drop.
 */
function resolveStyleHandleForExport(
  styleHandle: string | undefined,
  styleName: string | undefined,
  styleNameToHandle: Map<string, string>,
): string | undefined {
  if (styleHandle !== undefined) return styleHandle; // (a)
  if (styleName !== undefined) {
    // (b) — only resolves when a name→handle map was supplied (see note).
    const resolved = styleNameToHandle.get(styleName.toLowerCase());
    if (resolved !== undefined) return resolved;
  }
  return undefined; // (c) — no resolvable handle; 340 deliberately omitted.
}

/** The complex (text / shape) members of the `LinetypeElement` union. */
type ComplexLinetypeElement = Extract<LinetypeElement, { kind: "text" | "shape" }>;

/**
 * FIX (Codex PR-LT2 P2 — OMITTED transform-group round-trip): build the
 * optional complex-element transform groups (46 scale / 50 rotation / 44 x-offset
 * / 45 y-offset) for export, OMITTING a group the source did NOT carry so a
 * `source:"file"` element that lacked one never gains it (byte-exact round-trip).
 *
 * Presence is tracked at import via `had46/had50/had44/had45` (mirrors the
 * `hadTypeFlag` FIX-6 pattern). A group is emitted when EITHER the source carried
 * it (`hadXX`) OR `emitWhenAbsent` is true — the latter holds for AutoCAD-
 * canonical (seed / programmatic, `source !== "file"`) elements that AutoCAD
 * always writes the full transform for. Scale prefers the verbatim raw 46
 * (`scaleRaw`, preserves a dirty 0 / negative) over the validated positive
 * `scale` (FIX-3). Omitting a field (leaving it `undefined`) makes the dxf-io
 * writer skip the corresponding group (`if (el.scale !== undefined) …`).
 */
function complexTransformGroups(
  el: ComplexLinetypeElement,
  emitWhenAbsent: boolean,
): Pick<DxfLinetypeElement, "scale" | "rotation" | "offsetX" | "offsetY"> {
  return {
    ...(el.had46 || emitWhenAbsent ? { scale: el.scaleRaw ?? el.scale } : {}),
    ...(el.had50 || emitWhenAbsent ? { rotation: el.rotation } : {}),
    ...(el.had44 || emitWhenAbsent ? { offsetX: el.offsetX } : {}),
    ...(el.had45 || emitWhenAbsent ? { offsetY: el.offsetY } : {}),
  };
}

/**
 * Convert a typed `LinetypeElement` back into a `DxfLinetypeElement`.
 *
 * FIX-6: a SIMPLE element re-emits group 74 (as `elementType: 0`) when EITHER
 * its source had one (`hadTypeFlag`) OR `emitTypeFlagWhenAbsent` is true — the
 * latter holds for AutoCAD-canonical (seed / no-source) linetypes that AutoCAD
 * always writes a `74/0` for. A file-imported simple element that genuinely
 * LACKED a 74 has `hadTypeFlag` unset AND `emitTypeFlagWhenAbsent=false`, so
 * `elementType` stays undefined and the dxf-io writer OMITS 74 (byte parity).
 *
 * Complex (text/shape) elements similarly OMIT a transform group (46/50/44/45)
 * the source did NOT carry — see {@link complexTransformGroups}.
 */
function toDxfLinetypeElement(
  el: LinetypeElement,
  styleNameToHandle: Map<string, string>,
  emitTypeFlagWhenAbsent: boolean,
): DxfLinetypeElement {
  switch (el.kind) {
    case "dash":
    case "gap":
    case "dot": {
      // Emit a 74/0 when the source had one (`hadTypeFlag`) or this is an
      // AutoCAD-canonical entry that always carries 74 (`emitTypeFlagWhenAbsent`).
      const simpleType =
        el.hadTypeFlag || emitTypeFlagWhenAbsent ? { elementType: 0 } : {};
      // `dash` keeps its length; `gap` re-signs negative for code 49; `dot` is 0.
      const length = el.kind === "gap" ? -el.length : el.length;
      return { length, ...simpleType };
    }
    case "text": {
      // bit2 (text) + bit1 (absolute rotation) when rotationMode is absolute.
      const elementType = 2 | (el.rotationMode === "absolute" ? 1 : 0);
      const styleHandle = resolveStyleHandleForExport(
        el.styleHandle,
        el.styleName,
        styleNameToHandle,
      );
      return {
        length: el.length,
        elementType,
        ...(styleHandle !== undefined ? { styleHandle } : {}),
        ...complexTransformGroups(el, emitTypeFlagWhenAbsent),
        text: el.text,
      };
    }
    case "shape": {
      const elementType = 4 | (el.rotationMode === "absolute" ? 1 : 0);
      const styleHandle = resolveStyleHandleForExport(
        el.styleHandle,
        el.styleName,
        styleNameToHandle,
      );
      return {
        length: el.length,
        elementType,
        shapeNumber: el.shapeNumber,
        ...(styleHandle !== undefined ? { styleHandle } : {}),
        ...complexTransformGroups(el, emitTypeFlagWhenAbsent),
      };
    }
  }
}

/**
 * Convert a typed `Linetype` back into a `DxfLinetypeEntry`.
 *
 * Round-trip-exactly-once (plan §10.3): re-emit the original
 * `patternLengthRaw` / `elementCountRaw` (group 40 / 73) when present so a
 * plain round-trip is byte-exact; derive `40 = Σ|len|` / `73 = count` only for
 * entries with no raw value (seeded or freshly-edited). Reserved names are
 * forced to 0 elements / 0 length even if a dirty record carried stray ones.
 *
 * `styleNameToHandle` (optional) lets a complex element with only a
 * `styleName` (no raw `styleHandle`) re-emit its group-340 STYLE reference —
 * see {@link resolveStyleHandleForExport}. Defaults to empty (no resolution
 * possible) because `TextStyle` carries no handle today (PR-LT2 limitation).
 */
export function drawingLinetypeToDxfEntry(
  lt: Linetype,
  styleNameToHandle: Map<string, string> = new Map(),
): DxfLinetypeEntry {
  const isReserved = RESERVED_LINETYPE_NAMES.has(lt.name.toLowerCase());
  // FIX-6 — a simple element with no `hadTypeFlag` emits a 74/0 ONLY for an
  // AutoCAD-canonical entry (`source !== "file"`: the acad.lin seed, or a
  // legacy/in-memory linetype with no source). A `source:"file"` entry that
  // genuinely lacked a 74 on import must NOT gain one (byte parity, FIX-6).
  const emitTypeFlagWhenAbsent = lt.source !== "file";
  const elements = isReserved
    ? []
    : lt.elements.map((el) =>
        toDxfLinetypeElement(el, styleNameToHandle, emitTypeFlagWhenAbsent),
      );
  const derivedLength = isReserved
    ? 0
    : lt.elements.reduce((sum, el) => sum + Math.abs(el.length), 0);
  // Reserved names ALWAYS export 0 elements (above). A dirty reserved record
  // may carry stray raw `patternLengthRaw` / `elementCountRaw` (e.g. a
  // hand-edited `Continuous` with 73=5 / 40=99); re-using them here would
  // emit a record claiming elements exist with 0 actual elements — an
  // inconsistent 73 (Codex#2 P2). Force 40=0 / 73=0 for reserved names,
  // ignoring the raw values. Non-reserved names re-emit raw for byte-exact
  // round-trip, falling back to the derived values when no raw is present.
  const patternLength = isReserved ? 0 : (lt.patternLengthRaw ?? derivedLength);
  const elementCount = isReserved ? 0 : (lt.elementCountRaw ?? elements.length);
  const entry: DxfLinetypeEntry = {
    name: lt.name,
    description: lt.description,
    flags: lt.flags,
    // FIX-4 — re-emit the file's original group 72 when a non-65 raw value was
    // preserved (rare dirty/vendor file); otherwise the spec-canonical 65.
    alignment: lt.alignmentRaw ?? lt.alignment,
    patternLength,
    elementCount,
    elements,
    ...(lt.handle !== undefined ? { handle: lt.handle } : {}),
    ...(lt.ownerHandle !== undefined ? { ownerHandle: lt.ownerHandle } : {}),
    // FIX-B — thread the subclass-marker presence back to the dxf-io writer so
    // it re-emits `100/AcDbLinetypeTableRecord` only when the source had it.
    ...(lt.hadSubclassMarker !== undefined
      ? { hadSubclassMarker: lt.hadSubclassMarker }
      : {}),
    // Re-emit preserved vendor / unrecognised groups verbatim (Codex#2 P2).
    // The dxf-io writer appends these after the elements (its canonical slot
    // for round-tripped extras — see serializeLinetypeEntry).
    ...(lt.extraGroups && lt.extraGroups.length > 0
      ? {
          extraGroups: lt.extraGroups.map((g) => ({
            code: g.code,
            value: g.value,
          })),
        }
      : {}),
  };
  return entry;
}

/**
 * Decide whether a typed `Linetype` should be SERIALIZED on export (FIX-4 —
 * Codex#2 PR-LT2 P2). PR-LT1 forces the full acad.lin/ISO seed into
 * `document.linetypes` so the linetype refcheck resolves standard names, but
 * AutoCAD's LTYPE table only ever contains the linetypes actually DEFINED in
 * the file plus those USED by an object — it does NOT carry the ~38 unused
 * standard definitions. A plain round-trip must therefore drop unused
 * synthetic-seed entries. An entry is emitted IFF:
 *
 *   (a) it is a RESERVED name (ByLayer / ByBlock / Continuous) — AutoCAD always
 *       emits these three; OR
 *   (b) it is NOT a synthetic seed (`source !== "seed"`) — i.e. it came from the
 *       file's LTYPE table (`source:"file"`) or is a legacy/user/in-memory entry
 *       with no `source` (emit-always: conservative, never silently dropped); OR
 *   (c) it is actually REFERENCED by the drawing (its lowercased name is in
 *       `referencedNames`).
 *
 * Unused synthetic-seed entries (seed AND not reserved AND not referenced) are
 * skipped.
 */
function shouldExportLinetype(
  lt: Linetype,
  referencedNames: ReadonlySet<string>,
): boolean {
  const lc = lt.name.toLowerCase();
  if (RESERVED_LINETYPE_NAMES.has(lc)) return true; // (a)
  if (lt.source !== "seed") return true; // (b) file / user / legacy
  return referencedNames.has(lc); // (c) referenced synthetic-seed
}

/**
 * Convert the NAME-keyed `document.linetypes` table into `DxfLinetypeEntry[]`
 * for the writer. Insertion order of the record is preserved (matches the seed
 * + merge order from import). Unused synthetic-seed linetypes are SKIPPED
 * (FIX-4 — see {@link shouldExportLinetype}); pass the set of lowercased
 * referenced linetype names (`referencedNames`) so seed entries that ARE used
 * by an entity / layer / mleader-style still survive. Defaults to empty (no
 * references) — appropriate for a fresh doc with no drawing content.
 *
 * `styleNameToHandle` (optional) threads a STYLE name→handle map down to the
 * complex-element export so a `styleName`-only element can re-emit its 340.
 * Callers pass an empty map today (`TextStyle` has no handle — PR-LT2
 * limitation, see {@link resolveStyleHandleForExport}).
 */
export function exportLinetypes(
  linetypes: Record<string, Linetype> | undefined,
  referencedNames: ReadonlySet<string> = new Set(),
  styleNameToHandle: Map<string, string> = new Map(),
): DxfLinetypeEntry[] {
  if (!linetypes) return [];
  const out: DxfLinetypeEntry[] = [];
  for (const lt of Object.values(linetypes)) {
    if (!shouldExportLinetype(lt, referencedNames)) continue;
    out.push(drawingLinetypeToDxfEntry(lt, styleNameToHandle));
  }
  return out;
}
